import { Button } from "#components/ui/button"
import { Collapsible, CollapsibleTrigger } from "#components/ui/collapsible"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "#components/ui/sheet"
import { useIsMobile } from "#hooks/use-mobile"
import { cn } from "#lib/utils"
import { ArrowLeft, ChevronLeft, Menu, type LucideIcon } from "lucide-react"
import { useEffect, useState } from "react"

export interface InnerSideNavItem {
  id: string
  title: string
  icon?: LucideIcon
  anchors?: {
    id: string
    title: string
  }[]
}

export interface InnerSideNavProps {
  items: InnerSideNavItem[]
  activeId: string
  activeAnchorId?: string
  onItemClick: (id: string) => void
  sheetTitle?: string
  sheetDescription?: string
  showBackToOverview?: boolean
  onBackToOverview?: () => void
  backToOverviewLabel?: string
  onAnchorClick?: (anchorId: string) => void
}

export function InnerSideNav({
  items,
  activeId,
  activeAnchorId,
  onItemClick,
  sheetTitle = "Navigation",
  sheetDescription = "Select a section",
  showBackToOverview = false,
  onBackToOverview,
  backToOverviewLabel = "Back to Overview",
  onAnchorClick,
}: InnerSideNavProps) {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    if (!isMobile) return

    const handleScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop
      setIsScrolled(scrollY > 50)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isMobile])

  const handleItemClick = (id: string) => {
    onItemClick(id)
    if (isMobile) {
      setIsOpen(false)
    }
  }

  const navContent = (
    <div className="space-y-1">
      {showBackToOverview && onBackToOverview && (
        <Button
          variant="ghost"
          className="w-full justify-start mb-2"
          onClick={() => {
            onBackToOverview()
            if (isMobile) {
              setIsOpen(false)
            }
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {backToOverviewLabel}
        </Button>
      )}
      {showBackToOverview && onBackToOverview && <div className="my-2 border-t" />}
      {items.map((item) => {
        const Icon = item.icon
        const isActive = activeId === item.id
        const activeItem = items.find((i) => i.id === activeId)
        const showAnchors = isActive && activeItem?.anchors && activeItem.anchors.length > 0

        return (
          <div key={item.id} className="space-y-1">
            <Button
              variant={isActive ? "secondary" : "ghost"}
              className={cn("w-full justify-start", isActive && "bg-secondary font-medium")}
              onClick={() => handleItemClick(item.id)}
            >
              {Icon && <Icon className="mr-2 h-4 w-4" />}
              {item.title}
            </Button>
            {showAnchors && (
              <div className="ml-6 space-y-0.5 border-l pl-3">
                {activeItem.anchors?.map((anchor) => {
                  const isAnchorActive = activeAnchorId === anchor.id
                  return (
                    <Button
                      key={anchor.id}
                      variant={isAnchorActive ? "secondary" : "ghost"}
                      size="sm"
                      className={cn(
                        "w-full justify-start text-xs whitespace-normal text-left h-auto py-1.5 px-2",
                        isAnchorActive
                          ? "bg-secondary font-medium text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (onAnchorClick) {
                          onAnchorClick(anchor.id)
                        }
                        if (isMobile) {
                          setTimeout(() => {
                            setIsOpen(false)
                          }, 300)
                        }
                      }}
                    >
                      <span className="break-words">{anchor.title}</span>
                    </Button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  const mobileSheetTrigger = (
    <Button variant="outline" size="sm" className="w-full">
      <Menu className="mr-2 h-4 w-4" />
      {items.find((item) => item.id === activeId)?.title ?? "Menu"}
    </Button>
  )

  const mobileSheet = (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{mobileSheetTrigger}</SheetTrigger>
      <SheetContent side="left" className="w-64">
        <SheetHeader>
          <SheetTitle>{sheetTitle}</SheetTitle>
          <SheetDescription>{sheetDescription}</SheetDescription>
        </SheetHeader>
        <div className="mt-6">{navContent}</div>
      </SheetContent>
    </Sheet>
  )

  if (isMobile) {
    return (
      <>
        <div className={cn("mb-4 transition-all", isScrolled && "invisible")}>{mobileSheet}</div>
        <div
          className={cn(
            "fixed top-16 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-b transition-transform duration-300 px-4 pt-2 pb-2",
            isScrolled ? "translate-y-0" : "-translate-y-full"
          )}
        >
          {mobileSheet}
        </div>
      </>
    )
  }

  return (
    <div className="flex items-start gap-2">
      <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
        <nav
          className={cn(
            "flex-shrink-0 transition-all duration-300 self-start",
            isCollapsed ? "w-0 overflow-hidden" : "w-64"
          )}
        >
          {!isCollapsed && (
            <div className="sticky top-6 w-64 flex flex-col max-h-[calc(100vh-6rem)] bg-background">
              <div className="flex items-center justify-between mb-4 flex-shrink-0 px-4 pt-2">
                <span className="font-semibold text-sm">{sheetTitle}</span>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden">
                <div className="h-full overflow-y-auto overflow-x-hidden pr-2 -mr-2 px-4 pb-4">
                  {navContent}
                </div>
              </div>
            </div>
          )}
        </nav>
      </Collapsible>
      <Collapsible open={!isCollapsed} onOpenChange={(open) => setIsCollapsed(!open)}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ChevronLeft
              className={cn(
                "h-4 w-4 transition-transform duration-300",
                isCollapsed && "rotate-180"
              )}
            />
            <span className="sr-only">Toggle navigation</span>
          </Button>
        </CollapsibleTrigger>
      </Collapsible>
    </div>
  )
}
